
'use client';

import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './auth-context';
import { useFirebase } from '@/firebase';
import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  limit,
  documentId,
  onSnapshot,
  doc,
  updateDoc,
  setDoc,
  writeBatch,
  serverTimestamp,
  type QuerySnapshot,
} from 'firebase/firestore';
import { stripUndefinedDeep } from '@/lib/firestore-undefined';
import { CATALOG_MAX_DOCS, catalogAggregates } from '@/lib/live-catalog';
import { withoutUnsetDesignKeys } from '@/lib/product-design';
import { onSnapshotFromServer } from '@/firebase/firestore/server-first-snapshot';
import { confirmedSnapshots } from '@/lib/confirmed-snapshot';
import { PRODUCT_DELIVERY_DOC, PRODUCT_PRIVATE_COLLECTION, withLegacyAccessLinks } from '@/lib/product-access-links';
import type { AiUse } from '@/lib/ai-disclosure';
import type { CategoryFieldValues } from '@/lib/category-fields';
import type { PagePreview } from '@/lib/page-previews';

export type ProductImageObject = {
  src: string;
  path?: string;
  /** Original (pre-crop) image URL for non-destructive re-editing */
  originalSrc?: string;
  /** Storage path of the original (pre-crop) image, used for cleanup */
  originalPath?: string;
  /** Saved crop area in pixels, so re-editing starts from the previous state */
  croppedAreaPixels?: { x: number; y: number; width: number; height: number } | null;
  /** Zoom level used in the previous edit, reused for non-destructive editing */
  zoom?: number;
}
export type ProductMedia = {
    type: 'image' | 'video';
    src: string;
    path?: string;
    zoom?: number;
};

/**
 * Per-product appearance overrides set by the seller in the Design Preferences
 * section of the product editor. When `enabled` is false (or the whole object
 * is absent) the buyer's own theme preferences apply. When `enabled` is true,
 * the settings below override the buyer's, but only on this product's page.
 *
 * `starfield` decides the starfield on this page in dark mode; light mode has
 * no stars, and there it can only take the planets away from a buyer who
 * turned them on (see src/lib/cosmic-effects.ts). `accentBackground` is dark-mode only (a faint
 * tint fading into black). `accentColor` repaints buttons, links and
 * highlights in both themes and is the one setting here that is clamped for
 * contrast rather than simply applied.
 * See `clampAccentHex` in src/lib/color-utils.ts.
 *
 * The same shape as PageDesign in src/lib/product-design.ts, kept by hand:
 * PAGE_DESIGN_KEYS there, the hasOnly() list in firestore.rules and
 * DESIGN_KEYS in functions/src/lib/user-field-bounds.ts all name these keys.
 */
export type ProductDesign = {
  enabled?: boolean;
  starfield?: boolean;
  accentBackground?: boolean;
  /** `#RRGGBB`, or absent for no accent (supa.place's ink buttons and links). */
  accentColor?: string;
  /**
   * Force this product's page to light or dark, whatever the buyer's own
   * setting. Absent means the buyer's theme applies, which is the default and
   * what every product written before this shipped does.
   *
   * Note the interaction with the effects above: a seller who forces LIGHT
   * has hidden the accent background and the starfield (both dark mode only)
   * and, for most buyers, the planets (light mode shows them only to buyers
   * who turned them on there), whatever the toggles say. The editor says so rather than leaving them to
   * find out.
   *
   * `''` is the stored form of "no override", exactly as it is for
   * `accentColor` and for the same reason: the edit form merges design patches
   * and writes the whole product document, and `undefined` would either be
   * dropped by the merge — leaving the old value standing after the seller
   * switched it off — or rejected outright by Firestore. `resolveDesignOverride`
   * reads anything that is not exactly 'light' or 'dark' as no override, so the
   * empty string needs no special case downstream.
   */
  theme?: 'light' | 'dark' | '';
  /**
   * `#RRGGBB` for a solid page background, or absent/'' for the plain page.
   * While set it also decides light or dark (`theme` is ignored), so the text
   * stays readable on it; see src/lib/page-background.ts. Held as '' in the
   * forms and left off the write while unset, like the gradient keys.
   */
  backgroundColor?: string;
  /**
   * `#RRGGBB` for the accent background's own colour, or absent/'' to follow
   * the default (the buyer's own pick, else the page's accent). Not
   * clamped like `accentColor`: nothing is painted on it. The forms hold ''
   * until the seller explicitly picks one, for the same merge reason as
   * `theme`, but unlike `theme` the '' never reaches the document:
   * updateProduct and addProduct leave an unset gradient key out of the write
   * (see withoutUnsetDesignKeys in src/lib/product-design.ts for why).
   */
  gradientColor?: string;
  /** 'bottom' | 'top' for the edge the accent background rises from, or absent/'' for the buyer's own. */
  gradientDirection?: 'top' | 'bottom' | '';
  /**
   * The page's heading and body fonts: ids from src/lib/page-fonts.ts, or
   * absent/'' for the site font. Held as '' in the forms and left off the
   * write while unset, like the gradient keys.
   */
  headingFont?: string;
  bodyFont?: string;
  /**
   * The saved design ('1' to '5') this page follows, or absent/'' for none.
   * See src/lib/design-presets.ts. Left off the write while '' or while
   * `enabled` is off.
   */
  preset?: string;
};

export interface Product {
  id: string;
  originalId?: string;
  name: string;
  image: string | ProductImageObject;
  /**
   * Storage paths of the images embedded in `longDescription`.
   *
   * A companion field, not a source of truth: the description HTML is what
   * renders. It exists because an image referenced only by a URL inside a
   * string is invisible to every path collector we have — `extractAllFilePaths`
   * in functions and `collectStoragePaths` in src/lib/product-revisions.ts both
   * walk STRUCTURED fields — so without it, removing an image from the editor
   * or replacing it would leave the object in the bucket permanently, with no
   * error, no log line and no quota that counts it.
   *
   * Rewritten wholesale on every save from the description that was actually
   * stored, so it cannot drift from the HTML.
   */
  descriptionImagePaths?: string[];
  images: (string | ProductMedia)[];
  aiHint: string;
  price: number;
  discountedPrice?: number;
  activeDiscountId?: string;
  sales: number;
  revenue: number;
  rating: number;
  reviews: any[];
  reviewCount?: number;
  seller: string;
  sellerId: string;
  tags: string[];
  featured?: boolean;
  // Seller-controlled discovery toggle. When false, the product is hidden from
  // the marketplace, search, homepage featured, and recommendations, but stays
  // reachable via its direct product link and the seller's storefront/profile.
  // Absent (legacy products created before this field) is treated as listed.
  listedOnMarketplace?: boolean;
  productTags?: string[];
  // What the product is and what it is for (src/lib/catalog-taxonomy.ts): one
  // slug each from the fixed lists, firestore.rules refuses any other, and a
  // second purpose that is null or a different one. Absent on products saved
  // before 2026-10-20; readers use effectiveTaxonomyOf, which guesses for them.
  catalogType?: string;
  purpose?: string;
  secondaryPurpose?: string | null;
  // A teaching resource's year levels, subjects, kinds, curricula and formats
  // (src/lib/category-fields.ts), each a list from a closed list. Read through
  // categoryFieldsOf, which keeps them only while Education is a purpose.
  categoryFields?: CategoryFieldValues;
  // How much AI went into it, as the seller declares it (src/lib/ai-disclosure.ts).
  // Absent on products saved before 2026-10-22.
  aiUse?: AiUse;
  // Watermarked images of a few pages of its PDF and the PDF's length
  // (src/lib/page-previews.ts). The count is null once the previews are removed.
  pagePreviews?: PagePreview[];
  pdfPageCount?: number | null;
  isPurchased: boolean;
  isMerchant: boolean;
  isSubscription: boolean;
  isTieredPurchase: boolean;
  purchasedTierId?: number;
  canRefund: boolean;
  isCompeting: boolean;
  pricingModel: 'standard' | 'tiered' | 'pwyw' | 'subscription' | 'free';
  standardPrice?: number;
  priceTiers?: any[];
  subscriptionPrice?: number | null;
  subscriptionFrequency?: 'weekly' | 'fortnightly' | 'monthly' | '3-months' | 'annually';
  pwywMinPrice?: number | null;
  pwywMinPriceEnabled?: boolean;
  isSubscriptionCanceled?: boolean;
  longDescription: string;
  shortDescription: string;
  details: any;
  // `url` is intentionally omitted — gated product downloads must go through
  // /api/download which mints a short-lived signed URL from `path`. Persisting
  // the long-lived public getDownloadURL() would let anyone with the link
  // bypass ownership checks for the lifetime of the storage token.
  files: { [key: string]: { name: string, size: number, path: string }[] };
  // --- Shared file-pool model (supersedes the per-tier `files` map above) ---
  // Every deliverable is uploaded ONCE into this flat pool, keyed by a stable
  // `id`. Tier entitlement is expressed separately via `tierFiles` so a file is
  // never duplicated across tiers. Products created before this model only have
  // the legacy `files` map; readers must handle both. See /api/download.
  productFiles?: { id: string; name: string; size: number; path: string }[];
  // Per-tier download entitlement: maps a priceTier id (stringified) to the
  // set of `productFiles` ids that tier unlocks. Only meaningful for
  // `pricingModel === 'tiered'`. A buyer may download a pool file only if its
  // id is listed under the tier they purchased. Non-tiered products ignore this
  // (all pool files are downloadable).
  tierFiles?: { [tierId: string]: string[] };
  // Sum of every `file.size` in `files`. Denormalised so firestore.rules can
  // validate the aggregate against the seller's effective product cap at
  // write time — the rule engine can't iterate `files` to compute the sum
  // itself. enforceProductQuotaOrReject re-verifies against authoritative
  // storage metadata, so a forged value is still rejected by the trigger.
  filesTotalBytes?: number;
  // 'draft' is pre-publication work, private to its seller (firestore.rules
  // restricts reads) and excluded from every public surface because they all
  // filter on status === 'live'. A product leaves 'draft' at first publish and
  // can never return; withdrawing a published product is `listedOnMarketplace`.
  // 'rejected' is written by enforceProductQuotaOrReject (functions/src/index.ts)
  // when a product's files exceed the seller's tier cap. It was missing from this
  // union, which is how the products dashboard came to filter only for
  // live/draft/deleted and drop rejected listings out of every tab.
  //
  // 'archived' is the reversible middle ground between unlisting and deleting.
  // Unlisting (`listedOnMarketplace: false`) only hides a product from
  // discovery (the page still renders and checkout still accepts it), while
  // deleting is a one-way door: it sweeps the storage prefix, drops the parked
  // revision and cancels every subscription. Archiving stops the sale and keeps
  // everything else: files, revision, buyer access, and a route back.
  //
  // It is expressed as a STATUS rather than a flag on a live product precisely
  // because every public surface (marketplace, search, cart, product page,
  // public-catalog, and the checkout callable in functions/src/index.ts) already
  // gates on status === 'live'. A new status therefore fails CLOSED on any
  // surface nobody remembered to update, where a flag would fail open and keep
  // selling. That is the failure mode `listedOnMarketplace` already has, since
  // it cannot be filtered server-side and is applied in memory at each call
  // site.
  status:
    | 'draft'
    | 'live'
    | 'reviewing'
    | 'scheduled'
    | 'deleted'
    | 'rejected'
    | 'archived';
  scheduledFor?: Date | null;
  // Client-stamped on every save. Product docs carry no server-written
  // createdAt, and the Drafts tab needs something to sort and display, since a
  // draft's only meaningful timestamp is when its author last touched it.
  updatedAt?: Date | null;
  deletedAt?: Date | null;
  // Set alongside status 'rejected'. The reason is seller-facing copy naming the
  // cap that was exceeded and what to do about it, so the dashboard must show it:
  // without it a rejected product reads as broken rather than fixable.
  rejectionReason?: string | null;
  rejectedAt?: Date | null;
  // Stamped by `onProductUpdated` when a soft-deleted product had its
  // storage prefix cleared (no customers existed at delete time). Surfaces
  // a placeholder text on the product page instead of trying to load
  // cover/gallery URLs whose underlying files are gone.
  filesClearedAt?: Date | null;
  // True once the seller's account has been deleted. The product stays
  // downloadable for existing customers but the UI must render the seller
  // name as "deleted" and drop the profile link (the user doc is gone).
  sellerDeleted?: boolean;
  // Stamped alongside status 'archived'; cleared on restore. Only meaningful
  // while archived.
  archivedAt?: Date | null;
  isEdit?: boolean;
  /**
   * Where an archived product goes when it is restored.
   *
   * Archiving is offered from two places, so 'archived' is a merge point for two
   * different origins and restore needs to know which one it came from. This
   * field was declared here long before this feature and never read or written
   * by anything; it is now the restore target, written by the archive action and
   * pinned by firestore.rules for as long as the product stays archived.
   *
   * Pinning matters: without it a seller could archive a LIVE product, rewrite
   * originalStatus to 'draft' and restore it into the draft state, which is
   * exactly the live -> draft laundering the update rule's draft pin exists to
   * prevent (it would strand everyone who had already bought it).
   */
  originalStatus?: 'draft' | 'live';
  hasPendingEdit?: boolean;
  // Seller-set appearance overrides applied to this product's page only.
  design?: ProductDesign;
}

/**
 * The one place that decides where an archived product goes when restored, or
 * `null` when the document does not say and therefore cannot be restored.
 *
 * NULL IS NOT A FALLBACK, IT IS THE HONEST ANSWER. An earlier version of this
 * defaulted to 'live' whenever `originalStatus` was missing or unrecognised, on
 * the stated grounds that 'live' was "the only restore firestore.rules can
 * accept without it". That was wrong, and testing the rules against the
 * emulator is what showed it: the LEAVING-'archived' clause requires the new
 * status to equal `resource.data.originalStatus`, so with the field absent
 * `'live' != ''` and the write is denied — exactly as `'draft'` is. Neither
 * target works, so guessing one only converted a knowable condition into a
 * permission error, and the caller then showed the seller the 'live' branch of
 * its error copy ("needs a verified seller account"), which named a cause that
 * had nothing to do with it.
 *
 * Returning null lets the caller say the true thing instead. It cannot arise
 * through the UI — every archive write stamps the field in the same write that
 * sets the status, and the rules pin it for as long as the product stays
 * archived — so a null here describes a document written outside this feature,
 * by the Admin SDK or by hand.
 */
export function archiveRestoreTarget(
  product: Pick<Product, 'originalStatus'>,
): 'draft' | 'live' | null {
  if (product.originalStatus === 'draft') return 'draft';
  if (product.originalStatus === 'live') return 'live';
  return null;
}

export type ProductDiscount = {
  id: string;
  productId: string;
  sellerId: string;
  discountedPrice: number;
  basePrice: number;
  startMs: number;
  endMs: number;
  status: 'scheduled' | 'live' | 'expired' | 'deleted';
};

/**
 * How many catalog documents each `loadMore()` pulls. Also the size of one
 * streaming listener, so it must stay at or under Firestore's 30-value ceiling
 * for a `documentId() in [...]` filter.
 */
export const CATALOG_PAGE_SIZE = 12;

/**
 * How long the full-catalogue listener waits for the SERVER before the
 * marketplace says it cannot reach it. A little past the SDK's own ten seconds
 * before it falls back to the cache, so a slow answer still lands first.
 */
export const CATALOG_UNREACHABLE_MS = 12_000;

/**
 * Hard ceiling on the "full catalogue" listener the marketplace mounts.
 *
 * Not a page size — there is no next page. It is the point past which the
 * marketplace stops being a client-side-filtered list and has to become a
 * server-paginated one. Sized so it cannot bite at current volume; see the note
 * above the catalog listeners for why it exists at all.
 *
 * Defined in src/lib/live-catalog.ts, because the marketplace's server-rendered
 * first screen ranks the same slice (src/lib/marketplace-first-screen.ts).
 */
export { CATALOG_MAX_DOCS };

/** Firestore's hard limit on the number of values in an `in` filter. */
const MAX_IN_VALUES = 30;

interface ProductContextType {
  products: Map<string, Product>;
  /**
   * Non-expired (scheduled or active) discounts grouped by productId. Consumers
   * use this to (a) show live pricing the moment a discount's start time is
   * reached without waiting for the server sweep, and (b) guard against
   * stacking multiple discounts on the same product.
   *
   * Only populated while at least one mounted surface holds `useDiscounts()`.
   */
  productDiscounts: Map<string, ProductDiscount[]>;
  /**
   * Writes a product's fields. With `accessLinks`, the same batch also writes
   * them to the seller-only products/{id}/private/delivery, where a product's
   * paid links live (src/lib/product-access-links.ts), so the two cannot land
   * apart. The fields must then carry the public stand-in, never the links.
   */
  updateProduct: (
    productId: string,
    updatedFields: Partial<Product>,
    options?: { accessLinks?: readonly string[] },
  ) => Promise<void>;
  addProduct: (productId: string, product: Omit<Product, 'id'>) => Promise<void>;
  /** True once every source a mounted surface has asked for has delivered its first snapshot. */
  isLoaded: boolean;

  // --- demand registration (see the hooks at the bottom of this file) ---
  /** Retain every live product in one snapshot. Returns a release function. */
  retainFullCatalog: () => () => void;
  /** Retain only the first page of live products. Returns a release function. */
  retainCatalogPage: () => () => void;
  /** Retain the signed-in seller's own products, including drafts. */
  retainOwnProducts: () => () => void;
  /** Retain the discounts collection-group listener. */
  retainDiscounts: () => () => void;
  /**
   * True once the discounts listener has DELIVERED a snapshot, as opposed to
   * merely having been asked for. This is the gate that decides whether an
   * empty `productDiscounts` means "no sale is running" (safe to strip a stale
   * mirrored price) or "we do not know yet" (must not strip). See the note on
   * `products` below, and `useLiveDiscountedList` for the surfaces that run
   * their own query and need to make the same call.
   */
  discountsReady: boolean;

  /** True once the SERVER has answered the full-catalogue listener; see that listener. */
  catalogFullLoaded: boolean;
  /**
   * True when the server has not answered the full-catalogue listener within
   * CATALOG_UNREACHABLE_MS, or it failed: Firestore is blocked or too slow from
   * this network, as on many school networks. The marketplace then keeps the
   * server's first screen and says so, rather than calling itself empty.
   */
  catalogFullUnreachable: boolean;
  /**
   * True when the full-catalogue listener hit CATALOG_MAX_DOCS, i.e. the
   * marketplace is showing a subset. Surfaced so the truncation is visible on
   * screen rather than presenting a partial catalogue as the whole thing.
   */
  catalogTruncated: boolean;
  catalogPageLoaded: boolean;
  /**
   * True once the SERVER has answered the own-products listener (or it failed),
   * not on its first snapshot: that one can be this browser's cache, missing a
   * product written elsewhere. See the owner listener below.
   */
  ownProductsLoaded: boolean;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

/** Chunk a list of ids into `in`-filter-sized groups. */
function chunkIds(ids: string[]): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += MAX_IN_VALUES) {
    out.push(ids.slice(i, i + MAX_IN_VALUES));
  }
  return out;
}

/**
 * Put a raw product document's aggregates under the names the UI reads.
 *
 * Cloud Functions write `avgRating`, `totalReviews`, `totalSales` and
 * `totalRevenue`; older client code wrote `rating`, `reviewCount`, `sales` and
 * `revenue`, and the create form still initialises those four to 0 and never
 * touches them again. So a RAW document has `rating: 0` on a product with a
 * hundred five-star reviews, and anything reading `rating` off it is reading a
 * constant.
 *
 * EXPORTED because that is exactly what happened. /search runs its own
 * Firestore query rather than going through this provider, so it never passed
 * through this mapping: its "Minimum Rating" slider compared every product
 * against 0 and emptied the grid at any setting, and its "Top rated" sort
 * ordered by a constant, while the card beside them showed the true rating
 * (ProductCard reads avgRating/totalReviews through catalogAggregates, so it
 * was right even on a raw doc; it used to fetch the reviews subcollection
 * itself). Any surface that
 * queries `products` directly has to run its documents through this.
 *
 * @param id The document id.
 * @param data The document's raw fields.
 */
export function normalizeProductDoc(id: string, data: Record<string, any>): Product {
  // The mapping itself lives in src/lib/live-catalog.ts, so the marketplace's
  // server-rendered first screen reads the aggregates the same way.
  return {
    ...data,
    id,
    ...catalogAggregates(data),
  } as Product;
}

// Module scope so the standalone hooks at the bottom of this file share it with
// the provider.
function snapshotToMap(querySnapshot: any): Map<string, Product> {
  const productsMap = new Map<string, Product>();
  querySnapshot.forEach((docSnap: any) => {
    productsMap.set(docSnap.id, normalizeProductDoc(docSnap.id, docSnap.data()));
  });
  return productsMap;
}

function mergeMaps(parts: Iterable<Map<string, Product>>): Map<string, Product> {
  const out = new Map<string, Product>();
  for (const part of parts) {
    for (const [id, product] of part) out.set(id, product);
  }
  return out;
}

/**
 * Overlay the discount windows onto a set of products. If a window covers
 * "now", surface its discountedPrice and activeDiscountId so every card and
 * detail page renders the sale price the instant the minute arrives, without
 * waiting for the server sweep to mirror it onto the product document. If no
 * window covers now but the mirror still carries a stale discountedPrice, strip
 * it so we don't advertise a sale that has ended.
 *
 * `enabled` is false when nothing has retained the discounts listener. With an
 * empty `discounts` map we cannot tell "no sale is running" from "we never
 * asked", and stripping on the latter would hide a real sale price the server
 * has already written. In that case the mirror on the document is authoritative
 * and we pass the products through untouched.
 */
function applyLiveDiscounts(
  source: Map<string, Product>,
  discounts: Map<string, ProductDiscount[]>,
  enabled: boolean,
): Map<string, Product> {
  if (!enabled) return source;
  const now = Date.now();
  const next = new Map<string, Product>();
  for (const [id, product] of source) {
    const candidates = discounts.get(id) ?? [];
    const live = candidates.find((d) => d.startMs <= now && now < d.endMs);
    if (live) {
      next.set(id, {
        ...product,
        discountedPrice: live.discountedPrice,
        activeDiscountId: live.id,
      } as Product);
    } else if (product.discountedPrice !== undefined) {
      const { discountedPrice: _omit, activeDiscountId: _omit2, ...rest } =
        product as Product & { activeDiscountId?: string };
      next.set(id, rest as Product);
    } else {
      next.set(id, product);
    }
  }
  return next;
}

/**
 * A product write payload with its `design` map (if it carries one) in stored
 * form: an unset gradient colour or direction, font or preset link left out
 * rather than written as '', and the preset link dropped while the design is
 * not applied. Done here, at the two calls every product form saves through,
 * so the create form, the edit form and publishing a parked revision all get
 * it without either 3000-line page having to remember. The '' stays in the
 * forms' own state, where the design-patch merge needs it; only the write drops
 * it. See withoutUnsetDesignKeys for why (in short: an unset key must not make
 * a save depend on firestore.rules being deployed before the app).
 */
function withStoredDesign<T extends { design?: ProductDesign }>(fields: T): T {
  if (!fields.design) return fields;
  return { ...fields, design: withoutUnsetDesignKeys(fields.design) };
}

export function ProductProvider({ children }: { children: ReactNode }) {
  const { firestore } = useFirebase();
  const { authUser } = useAuth();

  // Every source below is demand-driven: nothing subscribes until a mounted
  // surface retains it. Previously the provider opened a listener over the
  // whole non-draft `products` collection plus a collection-group listener over
  // every discount document, on every route — including /login, /cart and the
  // dashboard, none of which render a catalog. Product docs carry
  // longDescription, files, productFiles and tierFiles, so that was the single
  // heaviest payload on the boot path, and it grew with the catalog.
  const [catalogWanted, setCatalogWanted] = useState(0);
  const [ownWanted, setOwnWanted] = useState(0);
  const [discountsWanted, setDiscountsWanted] = useState(0);

  const [ownProducts, setOwnProducts] = useState<Map<string, Product>>(new Map());
  const [ownLoaded, setOwnLoaded] = useState(false);
  const [discountDocs, setDiscountDocs] = useState<ProductDiscount[]>([]);
  // Has the discounts listener actually ANSWERED? `discountsWanted > 0` only
  // says a surface asked, and those are three states, not two: nobody asked, we
  // asked and are waiting, we have a real answer. applyLiveDiscounts treats an
  // empty map as proof that no window covers now and STRIPS a discountedPrice
  // the server genuinely mirrored — which is only sound in the third state.
  //
  // Collapsing the middle two is what turns any listener failure (a missing
  // collection-group index, a rules change, a transient permission error) into
  // "every live sale silently shows full price while checkout still charges the
  // sale price", because the error callback only logs. It also caused a brief
  // full-price flash on every first paint, before the first snapshot landed.
  const [discountsState, setDiscountsState] =
    useState<'idle' | 'pending' | 'ready'>('idle');
  // Minute ticker that triggers a re-render so a discount flips to live the
  // moment its start time is reached (or falls off at its end time), without
  // waiting for the server sweep. 15s is fine-grained enough that a seller
  // sees the sale go live within the same minute it's supposed to.
  const [clockTick, setClockTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setClockTick((t) => t + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  // --- Public catalog -------------------------------------------------------
  //
  // Two listeners, each a SINGLE atomic snapshot, mounted only on demand:
  //
  //   full  — every live product. For surfaces whose filters, facets and sort
  //           are computed across the whole catalog (the marketplace). One
  //           snapshot means one loading state and then a complete, stable
  //           grid: counts, the price-slider bound and the sort order can
  //           never shift under the user mid-browse.
  //   page  — the first CATALOG_PAGE_SIZE products, for surfaces that just
  //           need a handful of cards (the cart's recommendation strip) and
  //           would otherwise drag the entire catalog in behind them.
  //
  // An earlier revision cut the catalog into cursor-paged chunks with one
  // listener per page and pulled the remaining pages during idle time. That
  // was strictly worse for the marketplace: it needs every product anyway, so
  // paging turned one round trip into ceil(N/12) sequential ones AND made the
  // facet counts, slider bound and sort order visibly churn as each page
  // landed. Paging only pays off when you genuinely stop early.
  //
  // `status == 'live'` (rather than the old `!= 'draft'`) also keeps
  // soft-deleted, scheduled and in-review products out. Nothing public
  // rendered them before either, since every consumer filtered to 'live'.
  //
  // THE FULL LISTENER IS CAPPED. The reasoning above is still right — the
  // marketplace does need the whole catalogue to compute its facets — but
  // "whole catalogue" was previously expressed as a query with no limit at all,
  // and a realtime one at that. That is a cost curve, not a query: Firestore
  // bills per document read, so every visitor to /marketplace paid for one read
  // of every live product, and the browser held a live subscription over all of
  // them. At MVP volume that is nothing; at 10k products it is the single most
  // expensive thing the site does, and nothing in the code would have said so.
  //
  // CATALOG_MAX_DOCS bounds it. The cap is far above the current catalogue, so
  // it changes nothing today; what it does is stop the unbounded curve, and
  // make the ceiling visible (`catalogTruncated`) instead of letting products
  // silently stop appearing. Reaching it is the signal to move the marketplace
  // onto server-side pagination and search, which is the real fix and a
  // materially bigger change than this one.
  const [catalogFullWanted, setCatalogFullWanted] = useState(0);
  const [catalogPageWanted, setCatalogPageWanted] = useState(0);
  const [catalogFullDocs, setCatalogFullDocs] = useState<Map<string, Product>>(new Map());
  const [catalogPageDocs, setCatalogPageDocs] = useState<Map<string, Product>>(new Map());
  const [catalogFullLoaded, setCatalogFullLoaded] = useState(false);
  const [catalogFullUnreachable, setCatalogFullUnreachable] = useState(false);
  const [catalogTruncated, setCatalogTruncated] = useState(false);
  const [catalogPageLoaded, setCatalogPageLoaded] = useState(false);

  useEffect(() => {
    if (!firestore || catalogFullWanted === 0) return;
    // orderBy(documentId()) makes the truncation DETERMINISTIC. Without an
    // explicit order a limited query still returns some order, just not a
    // promised one, so which products fall off the end could change between
    // snapshots and the grid would reshuffle for no visible reason. Served by
    // the automatic single-field index (equality + __name__), same as the
    // paged listener below — no composite index needed.
    const q = query(
      collection(firestore, 'products'),
      where('status', '==', 'live'),
      orderBy(documentId()),
      limit(CATALOG_MAX_DOCS),
    );
    // ONLY THE SERVER'S ANSWER IS THE CATALOGUE. Where Firestore is blocked or
    // slow (many school networks), the SDK gives up on the server after about
    // ten seconds and answers from this browser's cache: often nothing at all.
    // Taken as the catalogue, that replaced the server-rendered first screen
    // with "The marketplace is empty" (2026-10-26 audit, item 13). So the cache
    // answer is not used (confirmedSnapshots, src/lib/confirmed-snapshot.ts:
    // a cached answer can also be PART of the catalogue, which would skew the
    // facets), `catalogFullLoaded` waits for the server, and a server that has
    // not answered in CATALOG_UNREACHABLE_MS, or an error, marks it unreachable
    // instead. A server answer that comes later still lands.
    const unreachableTimer = setTimeout(() => setCatalogFullUnreachable(true), CATALOG_UNREACHABLE_MS);
    const unsubscribe = onSnapshot(
      q,
      { includeMetadataChanges: true },
      confirmedSnapshots<QuerySnapshot>((snap, confirmed) => {
        if (!confirmed) return;
        clearTimeout(unreachableTimer);
        setCatalogFullUnreachable(false);
        setCatalogFullDocs(snapshotToMap(snap));
        setCatalogFullLoaded(true);
        // Loud, because the failure mode is silent: past the cap, products
        // simply stop appearing on the marketplace with nothing on screen or
        // in the logs to say why.
        if (snap.size >= CATALOG_MAX_DOCS) {
          setCatalogTruncated(true);
          console.warn(
            `[catalog] live products reached the ${CATALOG_MAX_DOCS}-document cap. ` +
            'The marketplace is no longer showing the whole catalogue — move it to ' +
            'server-side pagination and search.',
          );
        } else {
          setCatalogTruncated(false);
        }
      }),
      (error) => {
        console.error('Error fetching catalog:', error);
        clearTimeout(unreachableTimer);
        setCatalogFullUnreachable(true);
      },
    );
    return () => { clearTimeout(unreachableTimer); unsubscribe(); };
  }, [firestore, catalogFullWanted]);

  useEffect(() => {
    if (!firestore || catalogPageWanted === 0) return;
    // `orderBy(documentId())` rather than a business field: ordering on
    // `totalSales`/`createdAt` would silently drop every legacy document
    // missing that field, and the one consumer sorts client-side anyway.
    const q = query(
      collection(firestore, 'products'),
      where('status', '==', 'live'),
      orderBy(documentId()),
      limit(CATALOG_PAGE_SIZE),
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => { setCatalogPageDocs(snapshotToMap(snap)); setCatalogPageLoaded(true); },
      (error) => { console.error('Error fetching catalog page:', error); setCatalogPageLoaded(true); },
    );
    return () => unsubscribe();
  }, [firestore, catalogPageWanted]);

  // Documents survive release on purpose. Leaving the marketplace and coming
  // back re-attaches the listener, but the retained map means the grid renders
  // complete immediately instead of flashing its loading state again, and the
  // persistent Firestore cache (src/firebase/client.ts) answers the re-attach
  // from disk.
  const catalogDocs = useMemo(
    () => (catalogPageDocs.size === 0
      ? catalogFullDocs
      : mergeMaps([catalogPageDocs, catalogFullDocs])),
    [catalogPageDocs, catalogFullDocs],
  );

  // --- Owner listener -------------------------------------------------------
  // The signed-in seller's own products, which is the only way their drafts
  // reach the client. Overlaps the catalog pages on their published products;
  // the merge below dedupes by id and lets these win, because only this source
  // is guaranteed to carry a draft.
  // Whose snapshot `ownProducts` currently holds, so switching accounts can't
  // leave the previous seller's documents visible to the next one.
  const ownUidRef = useRef<string | null>(null);
  useEffect(() => {
    if (!firestore || ownWanted === 0 || !authUser?.uid) {
      setOwnProducts((prev) => (prev.size === 0 ? prev : new Map()));
      // "Loaded" describes the listener, never the absence of demand. This
      // previously reported true whenever nothing had retained the source yet,
      // which is exactly the state every consumer sees on its own first commit:
      // the retain effect bumps `ownWanted` in the same pass that this effect
      // runs with the stale 0. Consumers then read "loaded, and your product
      // isn't in the map" one render before the listener was even attached —
      // enough for the edit page to bounce a seller off their own product with
      // a "doesn't exist, or isn't one of yours" toast on every fresh load.
      ownUidRef.current = null;
      setOwnLoaded(false);
      return;
    }

    const uid = authUser.uid;
    if (ownUidRef.current !== uid) {
      ownUidRef.current = uid;
      setOwnProducts((prev) => (prev.size === 0 ? prev : new Map()));
      setOwnLoaded(false);
    }

    const q = query(collection(firestore, 'products'), where('sellerId', '==', uid));
    // Cached products show at once, but the source counts as loaded only once
    // the server has answered (src/lib/confirmed-snapshot.ts). The persistent
    // cache answers first, and its copy can lack a product written elsewhere (a
    // TPT import, another device): the editor reads "loaded, and not in the map"
    // as "not this seller's" and sends them away with "Product not found".
    const unsubscribe = onSnapshot(q, { includeMetadataChanges: true }, confirmedSnapshots<QuerySnapshot>((querySnapshot, confirmed) => {
        setOwnProducts(snapshotToMap(querySnapshot));
        if (confirmed) setOwnLoaded(true);
    }), (error) => {
      console.error("Error fetching own products:", error);
      setOwnLoaded(true);
    });

    return () => unsubscribe();
  }, [firestore, ownWanted, authUser?.uid]);

  const rawProducts = useMemo(() => {
    if (ownProducts.size === 0) return catalogDocs;
    if (catalogDocs.size === 0) return ownProducts;
    return mergeMaps([catalogDocs, ownProducts]);
  }, [catalogDocs, ownProducts]);

  // Stream the discounts that can still affect a price (public read is allowed
  // by rules) so every surface that renders a product can honor a live sale the
  // instant its start time arrives. Gated on demand: only surfaces that actually
  // render prices mount it.
  //
  // FILTERED SERVER-SIDE, and it has to be. This query was unconstrained, with
  // the status test below as the only filter, justified as "the set is small in
  // practice". Nothing keeps it small: sweepDiscounts only STAMPS
  // `status: "expired"` on a closed window (functions/src/index.ts), no path ever
  // deletes those rows, so the collection grows monotonically for the life of the
  // platform — and every one of them was shipped to every visitor, signed out
  // included, on every page that renders a price. The `in` needs the
  // discounts.status COLLECTION_GROUP fieldOverride in firestore.indexes.json:
  // a lone equality on a collection group is NOT served by the automatic
  // single-field indexes (those are COLLECTION scope), and the existing
  // (status, startDate) composite does not serve it either.
  //
  // 'scheduled' stays in deliberately. resolveItemPriceAud's header sets the
  // rule — the charge always reads the swept mirror, and only ADVERTISING runs
  // ahead of the window — and a badge that appears the minute a sale is due is
  // exactly what runs ahead. It does mean an upcoming sale's price is visible to
  // anyone before it opens; that is inherent to a client-side overlay, not an
  // oversight, and the alternative is up to a minute of dead air on every sale.
  //
  // Depends on the BOOLEAN, not the refcount. `discountsWanted` is a retain
  // count, so it moves on every price surface that mounts or unmounts — the
  // 1 -> 2 -> 1 walk from the marketplace to a product page and back. Keyed on
  // the number, this effect tore the listener down and re-subscribed on each of
  // those; now that the gate below is `discountsState === 'ready'`, it would
  // ALSO drop to 'pending' every time, un-applying the overlay on a transition
  // where nothing about the demand changed. The effect only ever asks whether
  // anybody wants the feed, so that is what it watches.
  const discountsRetained = discountsWanted > 0;
  useEffect(() => {
    if (!firestore || !discountsRetained) {
      setDiscountDocs((prev) => (prev.length === 0 ? prev : []));
      setDiscountsState('idle');
      return;
    }
    setDiscountsState('pending');
    const q = query(
      collectionGroup(firestore, 'discounts'),
      where('status', 'in', ['scheduled', 'live']),
    );
    // From the server, never from this browser's cache: offline (or with
    // Firestore unreachable) the cache answers with whatever it holds, often
    // nothing, and an empty answer marked 'ready' strips every mirrored sale
    // price, which is the failure the three states above exist to prevent. A
    // server-rendered product page shows that mirror before any listener runs,
    // so the strip would also visibly undo its first paint. Until the server
    // answers, the state stays 'pending' and the mirror stands.
    const unsubscribe = onSnapshotFromServer(q, (snap) => {
      const list: ProductDiscount[] = [];
      snap.forEach((docSnap) => {
        const data = docSnap.data() as Record<string, unknown>;
        const status = data.status;
        if (status !== 'scheduled' && status !== 'live') return;
        const productId = docSnap.ref.parent.parent?.id;
        if (!productId) return;
        const start = data.startDate as { toMillis?: () => number } | undefined;
        const end = data.endDate as { toMillis?: () => number } | undefined;
        list.push({
          id: docSnap.id,
          productId,
          sellerId: typeof data.sellerId === 'string' ? data.sellerId : '',
          discountedPrice: typeof data.discountedPrice === 'number' ? data.discountedPrice : 0,
          basePrice: typeof data.basePrice === 'number' ? data.basePrice : 0,
          startMs: typeof start?.toMillis === 'function' ? start.toMillis() : 0,
          endMs: typeof end?.toMillis === 'function' ? end.toMillis() : 0,
          status: status as 'scheduled' | 'live',
        });
      });
      setDiscountDocs(list);
      setDiscountsState('ready');
    }, (error) => {
      // Fail back to the server mirror rather than to "no sale is running".
      // The mirror is what resolveItemPriceAud charges off, so a product keeps
      // showing and charging the same number; the only thing lost is the
      // up-to-a-minute head start the overlay gives a window that is opening.
      //
      // The likeliest cause is the discounts.status COLLECTION_GROUP field
      // override in firestore.indexes.json not being deployed: a lone equality
      // on a collection group is NOT served by the automatic single-field
      // indexes (those are COLLECTION scope), so the query throws
      // failed-precondition on every snapshot until it exists.
      console.error('Error fetching discounts (falling back to the server-mirrored price):', error);
      setDiscountDocs((prev) => (prev.length === 0 ? prev : []));
      setDiscountsState('idle');
    });
    return () => unsubscribe();
  }, [firestore, discountsRetained]);

  const productDiscounts = useMemo(() => {
    const map = new Map<string, ProductDiscount[]>();
    for (const d of discountDocs) {
      // Surface only windows that have not yet ended. Expired windows are
      // already filtered by status above, but a "scheduled" record could in
      // theory sit past its endMs if the sweep is lagging — ignore those too.
      if (d.endMs && d.endMs <= Date.now()) continue;
      const arr = map.get(d.productId);
      if (arr) arr.push(d);
      else map.set(d.productId, [d]);
    }
    return map;
    // clockTick intentionally in deps: needed to drop windows that just ended.
    //
    // IT ALSO CARRIES THE TICK OUT OF THIS FILE. Rebuilding gives the map a new
    // identity every 15s, which is the only thing that re-runs the overlay in
    // `useLiveDiscountedList` for a surface holding its own product query
    // (/search). Memoising this on `discountDocs` alone would look like a free
    // win and would silently freeze that surface's sale prices at whatever was
    // true when its query last changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discountDocs, clockTick]);

  // Re-render-time derivation of the products map. If a discount's window
  // covers "now", surface its discountedPrice and activeDiscountId on the
  // product object so every card/detail page renders the sale price the
  // instant the minute arrives. If no window covers now but the server mirror
  // still carries a stale discountedPrice, strip it so we don't show a sale
  // that is no longer running.
  //
  // Skipped entirely unless the listener has DELIVERED a snapshot: with
  // `productDiscounts` empty we cannot tell "no sale is running" from "we never
  // asked", "we are still waiting" or "the query failed", and stripping on any
  // of the last three would hide a genuine sale price that the server already
  // mirrored onto the product document — while checkout went on charging it.
  // That is why the gate is `discountsState === 'ready'` and not
  // `discountsWanted > 0`.
  const products = useMemo(
    () => applyLiveDiscounts(rawProducts, productDiscounts, discountsState === 'ready'),
    // clockTick intentionally in deps so we flip the live price the minute a
    // window opens or closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawProducts, productDiscounts, clockTick, discountsState],
  );
  
  const updateProduct = useCallback(async (
    productId: string,
    updatedFields: Partial<Product>,
    options?: { accessLinks?: readonly string[] },
  ) => {
    if (!firestore) return;
    const productRef = doc(firestore, 'products', productId);
    const fields = stripUndefinedDeep(withStoredDesign(updatedFields));
    if (!options?.accessLinks) {
      await updateDoc(productRef, fields);
    } else {
      const batch = writeBatch(firestore);
      batch.update(productRef, fields);
      batch.set(doc(firestore, 'products', productId, PRODUCT_PRIVATE_COLLECTION, PRODUCT_DELIVERY_DOC), {
        links: [...options.accessLinks],
        updatedAt: serverTimestamp(),
      });
      try {
        await batch.commit();
      } catch (err) {
        // Rules that predate the delivery document have no match for it, so
        // they refuse the whole batch. Saving has to keep working in that
        // window, because the web app can be live before the rules are, so the
        // product is then written as it was before the move, links included
        // (withLegacyAccessLinks). Rules that know the delivery document refuse
        // links on the product, so this cannot put them back after the move: a
        // batch refused for any other reason is refused again here, and that
        // error is what the editor shows.
        if ((err as { code?: unknown } | null)?.code !== 'permission-denied') throw err;
        await updateDoc(productRef, withLegacyAccessLinks(fields, options.accessLinks));
      }
    }
    // Analytics subcollection cleanup is intentionally NOT performed client-side.
    // The firestore rule `products/{id}/analytics` has `allow write: if false`,
    // so a batch delete here fails with "missing or insufficient permissions"
    // whenever the product has existing analytics documents (i.e. had sales).
    // If analytics cleanup is needed it must happen in a Cloud Function.
  }, [firestore]);

  const addProduct = useCallback(async (productId: string, productData: Omit<Product, 'id'>) => {
    if (!firestore) return;
    await setDoc(doc(firestore, 'products', productId), stripUndefinedDeep(withStoredDesign(productData)));
  }, [firestore]);

  // Retain/release counters. Each returns a one-shot release so a consumer can
  // hand it straight back from a useEffect cleanup, and a double-release (React
  // StrictMode's double-invoked effects) can't drive the count negative.
  const retainFullCatalog = useCallback(() => {
    setCatalogFullWanted((n) => n + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setCatalogFullWanted((n) => Math.max(0, n - 1));
    };
  }, []);

  const retainCatalogPage = useCallback(() => {
    setCatalogPageWanted((n) => n + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setCatalogPageWanted((n) => Math.max(0, n - 1));
    };
  }, []);

  const retainOwnProducts = useCallback(() => {
    setOwnWanted((n) => n + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setOwnWanted((n) => Math.max(0, n - 1));
    };
  }, []);

  const retainDiscounts = useCallback(() => {
    setDiscountsWanted((n) => n + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setDiscountsWanted((n) => Math.max(0, n - 1));
    };
  }, []);

  // "Loaded" means every source somebody actually asked for has reported back.
  // A surface that retains nothing is loaded by definition.
  const isLoaded =
    (catalogFullWanted === 0 || catalogFullLoaded)
    && (catalogPageWanted === 0 || catalogPageLoaded)
    && (ownWanted === 0 || ownLoaded);

  const value: ProductContextType = useMemo(() => ({
    products,
    productDiscounts,
    updateProduct,
    addProduct,
    isLoaded,
    retainFullCatalog,
    retainCatalogPage,
    retainOwnProducts,
    retainDiscounts,
    discountsReady: discountsState === 'ready',
    catalogFullLoaded,
    catalogFullUnreachable,
    catalogTruncated,
    catalogPageLoaded,
    ownProductsLoaded: ownLoaded,
  }), [
    products, productDiscounts, updateProduct, addProduct, isLoaded,
    retainFullCatalog, retainCatalogPage, retainOwnProducts, retainDiscounts,
    discountsState, catalogFullLoaded, catalogFullUnreachable, catalogTruncated, catalogPageLoaded, ownLoaded,
  ]);

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
}

/**
 * Passive read of whatever products are currently loaded. Does NOT cause
 * anything to load — pair it with one of the retaining hooks below, or use
 * those hooks' own return values.
 */
export function useProducts() {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
}

/**
 * The public catalog.
 *
 * `full: true` streams every live product in one snapshot. Use it wherever
 * filters, facets or sort order are computed across the whole catalog, so that
 * `isLoaded` means "the set is complete" and the UI can hold a single loading
 * state rather than mutating as data trickles in.
 *
 * Without `full`, only the first page is streamed — for surfaces that render a
 * handful of cards and have no business pulling the whole marketplace in.
 */
export function useCatalog(options?: { full?: boolean }) {
  const ctx = useProducts();
  const { retainFullCatalog, retainCatalogPage } = ctx;
  const full = options?.full === true;

  useEffect(
    () => (full ? retainFullCatalog() : retainCatalogPage()),
    [full, retainFullCatalog, retainCatalogPage],
  );

  return {
    products: ctx.products,
    isLoaded: full ? ctx.catalogFullLoaded : ctx.catalogPageLoaded,
    truncated: full ? ctx.catalogTruncated : false,
    /** The server has not answered (see catalogFullUnreachable); full catalogue only. */
    unreachable: full ? ctx.catalogFullUnreachable && !ctx.catalogFullLoaded : false,
  };
}

/**
 * The signed-in seller's own products, drafts included. They show as soon as
 * this browser's cache has them, but `isLoaded` turns true only once the server
 * has answered, so a product missing from a loaded list really is missing (or
 * not this seller's). Wait for it before acting on an absence.
 */
export function useOwnProducts() {
  const ctx = useProducts();
  const { retainOwnProducts } = ctx;
  useEffect(() => retainOwnProducts(), [retainOwnProducts]);
  return { products: ctx.products, isLoaded: ctx.ownProductsLoaded };
}

/** Mount the discounts collection-group listener for as long as this surface lives. */
export function useDiscounts() {
  const ctx = useProducts();
  const { retainDiscounts } = ctx;
  useEffect(() => retainDiscounts(), [retainDiscounts]);
  return ctx.productDiscounts;
}

/**
 * The live-discount overlay, for a surface that runs its OWN product query.
 *
 * WHY. `applyLiveDiscounts` runs inside the provider, so every surface fed by
 * the context gets a sale price the minute a window opens and loses it the
 * minute the window closes, without waiting for `sweepDiscounts` to mirror
 * either onto the product document. A surface that queries Firestore directly
 * gets neither: it renders the mirror, so for up to the sweep's one-minute
 * cadence it advertises a sale that has not started, or one that has ended.
 * /search was doing exactly that. On a platform that is Merchant of Record for
 * the charge, a shown price that is not the charged price is worth a hook.
 *
 * Takes and returns an ARRAY because that is what `useCollection` hands its
 * callers; the provider's own path stays on the Map it already builds.
 *
 * The readiness gate is `discountsReady`, not "did we ask": with an empty
 * discounts map we cannot tell "no sale is running" from "still waiting" or
 * "the query failed", and stripping on either of the last two would hide a
 * genuine sale price the server had already mirrored, while checkout went on
 * charging it. Until the listener reports, the list is passed through untouched.
 *
 * @param products Whatever the caller's own query returned.
 * @returns The same products, with sale prices overlaid and stale ones removed.
 */
export function useLiveDiscountedList<P extends Product>(products: readonly P[]): readonly P[] {
  const { retainDiscounts, productDiscounts, discountsReady } = useProducts();
  useEffect(() => retainDiscounts(), [retainDiscounts]);

  return useMemo(() => {
    if (!discountsReady || products.length === 0) return products;
    const source = new Map<string, Product>();
    for (const product of products) source.set(product.id, product);
    const overlaid = applyLiveDiscounts(source, productDiscounts, true);
    // Rebuilt from the INPUT order: applyLiveDiscounts preserves insertion
    // order, but the caller's sort runs afterwards and should not depend on
    // that being true.
    return products.map((product) => (overlaid.get(product.id) as P | undefined) ?? product);
    // No clock of its own, and it does not need one: `productDiscounts` is
    // rebuilt on the provider's 15s tick, so it arrives here with a new identity
    // every tick and a window that opens or closes under an idle page is picked
    // up on the next one. That is a real dependency between two memos in this
    // file rather than a coincidence, which is why the one above says so too.
  }, [products, productDiscounts, discountsReady]);
}

/**
 * Load and stream a specific set of products by id — the library, cart,
 * subscriptions and product-detail surfaces, which know exactly which products
 * they need and have no use for the catalog.
 *
 * Ids are chunked into Firestore-legal `in` filters, one listener per chunk.
 * The id list is normalised to a stable key so a caller re-deriving the array
 * each render doesn't churn subscriptions.
 *
 * `seed` is the server's own read of exactly these products (the product page
 * passes it; see src/lib/product-page-seed.ts). With it the hook starts LOADED,
 * holding those documents, so the first render, on the server and in the
 * browser alike, is the real page instead of a loading state; and the
 * listeners wait for the server rather than this browser's cache before they
 * replace it (onSnapshotFromServer). It is read once, on mount, and only for
 * the id set it was built for.
 */
export function useProductsByIds(
  ids: readonly string[],
  options?: { withDiscounts?: boolean; seed?: ReadonlyMap<string, Product> },
) {
  const { firestore } = useFirebase();
  const { retainDiscounts, productDiscounts, discountsReady } = useProducts();
  const withDiscounts = options?.withDiscounts === true;
  const key = useMemo(
    () => Array.from(new Set(ids.filter(Boolean))).sort().join(','),
    [ids],
  );
  const [seed] = useState(() => {
    const docs = options?.seed;
    if (!docs || docs.size === 0 || !key) return null;
    return key.split(',').every((id) => docs.has(id)) ? { key, docs: new Map(docs) } : null;
  });
  const [raw, setRaw] = useState<Map<string, Product>>(() => (seed ? new Map(seed.docs) : new Map()));
  const [isLoaded, setIsLoaded] = useState(seed !== null);

  useEffect(() => {
    if (!withDiscounts) return;
    return retainDiscounts();
  }, [withDiscounts, retainDiscounts]);

  useEffect(() => {
    const list = key ? key.split(',') : [];
    if (!firestore || list.length === 0) {
      setRaw((prev) => (prev.size === 0 ? prev : new Map()));
      setIsLoaded(true);
      return;
    }
    const seeded = seed !== null && seed.key === key ? seed.docs : null;
    // Seeded: the server's copy is on screen and stays there until the server
    // answers, so there is no loading state to go back to.
    if (!seeded) setIsLoaded(false);
    const chunks = chunkIds(list);
    const perChunk = new Map<number, Map<string, Product>>();
    const settled = new Set<number>();
    if (seeded) {
      chunks.forEach((chunk, index) => {
        const part = new Map<string, Product>();
        for (const id of chunk) {
          const product = seeded.get(id);
          if (product) part.set(id, product);
        }
        perChunk.set(index, part);
        settled.add(index);
      });
    }
    const publish = (index: number, docs: Map<string, Product>) => {
      perChunk.set(index, docs);
      settled.add(index);
      setRaw(mergeMaps(perChunk.values()));
      if (settled.size === chunks.length) setIsLoaded(true);
    };
    const unsubs = chunks.map((chunk, index) => {
      const q = query(collection(firestore, 'products'), where(documentId(), 'in', chunk));
      const onNext = (snap: QuerySnapshot) => publish(index, snapshotToMap(snap));
      const onError = (error: unknown) => {
        // A single unreadable id (someone else's draft) fails the whole
        // chunk. Publish it empty so callers resolve to "not found" instead
        // of spinning forever.
        console.error('Error fetching products by id:', error);
        publish(index, new Map());
      };
      return seeded ? onSnapshotFromServer(q, onNext, onError) : onSnapshot(q, onNext, onError);
    });
    return () => unsubs.forEach((unsub) => unsub());
    // `seed` is state that never changes after mount.
  }, [firestore, key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gated on the discounts listener having ANSWERED, exactly as the provider's
  // own catalogue is (see `products` in ProductProvider): with no answer yet an
  // empty discounts map would strip a sale price the server has mirrored onto
  // the document and that checkout will charge, painting full price until the
  // listener landed, and for good if it failed. A server-rendered first paint
  // has no listener at all, so this is also what keeps the sale price in it.
  const products = useMemo(
    () => applyLiveDiscounts(raw, productDiscounts, withDiscounts && discountsReady),
    [raw, productDiscounts, withDiscounts, discountsReady],
  );

  // The documents EXACTLY as Firestore holds them, with no discount overlay.
  //
  // This is what the server charges from: resolveItemPriceAud reads
  // `productData.discountedPrice` straight off the doc, and that mirror is
  // written by the once-a-minute sweepDiscounts. The overlay above deliberately
  // runs ahead of that sweep so a sale badge appears the minute it is due — good
  // for advertising, wrong for any claim about what a card will be charged.
  //
  // The cart needs both: the overlay for what it SHOWS, and this for its "the
  // seller has repriced this since you added it" check, which is a statement
  // about the charge. Handing back a second map costs nothing — it is the same
  // state object the overlay is derived from.
  return { products, rawProducts: raw, isLoaded };
}

/**
 * One seller's published products — the public storefront / profile page.
 *
 * `seed` is the server's own read of the same query for the same seller (the
 * storefront passes it; see src/lib/storefront-seed.ts), and works as it does
 * for useProductsByIds: the hook starts loaded with those products, and the
 * listener waits for the server rather than this browser's cache before it
 * replaces them. Read once, on mount, and only for the seller it was built for.
 */
export function useSellerProducts(
  sellerId: string | null | undefined,
  options?: {
    withDiscounts?: boolean;
    seed?: { sellerId: string; products: ReadonlyMap<string, Product> };
  },
) {
  const { firestore } = useFirebase();
  const { retainDiscounts, productDiscounts, discountsReady } = useProducts();
  const withDiscounts = options?.withDiscounts === true;
  const [seed] = useState(() => (options?.seed?.sellerId ? options.seed : null));
  const seededAtMount = seed !== null && seed.sellerId === sellerId;
  const [raw, setRaw] = useState<Map<string, Product>>(() => (seededAtMount ? new Map(seed.products) : new Map()));
  const [isLoaded, setIsLoaded] = useState(seededAtMount);

  useEffect(() => {
    if (!withDiscounts) return;
    return retainDiscounts();
  }, [withDiscounts, retainDiscounts]);

  useEffect(() => {
    if (!firestore || !sellerId) {
      setRaw((prev) => (prev.size === 0 ? prev : new Map()));
      setIsLoaded(!sellerId);
      return;
    }
    const seeded = seed !== null && seed.sellerId === sellerId;
    // Seeded: the server's copy is on screen and stays there until the server
    // answers, so there is no loading state to go back to.
    if (!seeded) setIsLoaded(false);
    // Both filters are equalities, which Firestore serves by merging the two
    // single-field indexes — no composite index to deploy. `status == 'live'`
    // is required for correctness, not just tidiness: an unfiltered sellerId
    // query would try to return that seller's drafts and the read rule would
    // reject the query as a whole for anyone but the owner.
    const q = query(
      collection(firestore, 'products'),
      where('sellerId', '==', sellerId),
      where('status', '==', 'live'),
    );
    const onNext = (snap: QuerySnapshot) => { setRaw(snapshotToMap(snap)); setIsLoaded(true); };
    const onError = (error: unknown) => { console.error('Error fetching seller products:', error); setIsLoaded(true); };
    const unsubscribe = seeded ? onSnapshotFromServer(q, onNext, onError) : onSnapshot(q, onNext, onError);
    return () => unsubscribe();
    // `seed` is state that never changes after mount.
  }, [firestore, sellerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gated on the discounts listener having answered; see useProductsByIds.
  const products = useMemo(
    () => applyLiveDiscounts(raw, productDiscounts, withDiscounts && discountsReady),
    [raw, productDiscounts, withDiscounts, discountsReady],
  );

  return { products, isLoaded };
}

/**
 * Featured products for the landing page. A dedicated query rather than a slice
 * of the catalog: a featured product is not necessarily on page one, and the
 * landing page has no other reason to pull a catalog page at all.
 */
export function useFeaturedProducts(max = 8) {
  const { firestore } = useFirebase();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!firestore) return;
    const q = query(
      collection(firestore, 'products'),
      where('status', '==', 'live'),
      where('featured', '==', true),
      limit(max),
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        // `listedOnMarketplace` can't be filtered server-side: legacy documents
        // omit the field entirely and an equality filter would drop them.
        setProducts(
          Array.from(snapshotToMap(snap).values())
            .filter((p) => (p as { listedOnMarketplace?: boolean }).listedOnMarketplace !== false),
        );
        setIsLoaded(true);
      },
      (error) => { console.error('Error fetching featured products:', error); setIsLoaded(true); },
    );
    return () => unsubscribe();
  }, [firestore, max]);

  return { products, isLoaded };
}
